package no.vareflow.web

import no.vareflow.app.domain.t
import no.vareflow.app.domain.AppLanguage
import no.vareflow.app.domain.I18n
import no.vareflow.app.domain.CompanyCode
import no.vareflow.app.domain.CompanyLogin
import no.vareflow.app.domain.formatQuantity

/** Mer → Firebase-synkronisering (also the header pill): state, queue, conflicts, refused counts. */
fun WebApp.showSyncDialog() {
    openDialog(t("Synkronisering", "Sync")) { close ->
        val m = cloud.membership
        div("strong " + when (cloud.state) {
            CloudState.ONLINE -> "ok-text"
            CloudState.ERROR -> "error-text"
            else -> "warning-text"
        }, cloud.state.label)
        p(t("Kø: ${cloud.pendingWrites} · Konflikter: ${cloud.conflicts.size}", "Queue: ${cloud.pendingWrites} · Conflicts: ${cloud.conflicts.size}"), "small")
        if (m != null) p("${m.companyName} · ${m.locationName} · ${CompanyLogin.displayLogin(m.email)}", "muted tiny")
        cloud.lastError?.let { div("error-text tiny", it) }
        div("button-row") {
            button(t("Prøv sync", "Retry sync"), "btn primary") {
                cloud.flushOutbox()
                toast(t("Synkronisering forsøkes på nytt.", "Trying to sync again."))
            }
        }
        if (cloud.conflicts.isEmpty()) p(t("Ingen åpne konflikter.", "No open conflicts."), "muted small")
        cloud.conflicts.toList().forEach { conflict ->
            val product = core.products.firstOrNull { it.id == conflict.productId }?.name ?: conflict.productName
            val department = core.departmentsForSession(conflict.sessionId).firstOrNull { it.id == conflict.departmentId }?.name ?: conflict.departmentId
            div("card conflict-card") {
                div("strong", product)
                div("muted tiny", department)
                div("small", "${t("Lokal", "Local")}: ${conflict.localQuantity?.let(::formatQuantity) ?: t("ikke telt", "not counted")} · ${t("Sky", "Cloud")}: ${conflict.remoteQuantity?.let(::formatQuantity) ?: t("ikke telt", "not counted")}")
                if (conflict.localNote.isNotBlank() || conflict.remoteNote.isNotBlank()) {
                    div("tiny", "${t("Lokal kommentar", "Local comment")}: ${conflict.localNote.ifBlank { "–" }}")
                    div("tiny", "${t("Sky-kommentar", "Cloud comment")}: ${conflict.remoteNote.ifBlank { "–" }}")
                }
                div("button-row") {
                    button(t("Bruk sky", "Use cloud"), "btn outline") { cloud.resolveConflictKeepCloud(conflict); requestRender() }
                    button(t("Behold lokal", "Keep local"), "btn primary") { cloud.resolveConflictKeepLocal(conflict); requestRender() }
                }
            }
        }
        if (cloud.rejected.isNotEmpty()) {
            h2(t("Avviste tellinger", "Rejected counts"), "dialog-subtitle")
            cloud.rejected.take(10).forEach { r ->
                val product = core.products.firstOrNull { it.id == r.write.productId }?.name ?: r.write.productId
                div("small", "$product: ${r.write.quantity?.let(::formatQuantity) ?: t("tømt", "cleared")} – ${r.reason}")
            }
        }
        dialogActions { button(t("Lukk", "Close"), "btn text") { close() } }
    }
}

/** Mer → Bytt bedrift: the companies this person belongs to. */
fun WebApp.showCompanySwitcher() {
    var choices: List<CompanyChoice>? = null
    var error: String? = null
    var busy = false
    cloud.listCompanies { result ->
        result.onSuccess { choices = it }.onFailure { error = it.message }
        requestRender()
    }
    openDialog(t("Bytt bedrift", "Switch company")) { close ->
        val current = cloud.membership?.companyId
        when {
            error != null -> errorText(error)
            choices == null -> p(t("Henter bedrifter …", "Loading companies …"), "muted small")
            choices!!.isEmpty() -> p(t("Ingen bedrifter.", "No companies."), "muted small")
            else -> div("list") {
                choices!!.forEach { choice ->
                    val isCurrent = choice.companyId == current
                    el("button", "card nav-card company-choice") {
                        attr("type", "button")
                        node.disabled = isCurrent || busy
                        div("company-monogram", CompanyCode.monogram(choice.name))
                        div("grow") {
                            div("card-title", choice.name)
                            div("muted small", (if (isCurrent) t("Åpen nå · ", "Open now · ") else "") + when (choice.role.uppercase()) { "ADMIN" -> "Admin"; "LEADER" -> t("Leder", "Leader"); else -> t("Ansatt", "Employee") })
                        }
                        onClick {
                            if (isCurrent || busy) return@onClick
                            busy = true
                            requestRender()
                            flushPendingCounts()
                            cloud.openCompany(choice.companyId) { result ->
                                busy = false
                                result.onSuccess { m ->
                                    close()
                                    activateCloud(m, cloud.isRemembered())
                                    toast(t("${m.companyName} er åpnet.", "${m.companyName} is open."))
                                }.onFailure { error = it.message; requestRender() }
                            }
                        }
                    }
                }
            }
        }
        dialogActions { button(t("Lukk", "Close"), "btn text") { close() } }
    }
}

/** Mer → Ny bedrift (owner only). */
fun WebApp.showNewCompanyDialog() {
    var name = ""
    var place = ""
    var error: String? = null
    var busy = false
    openDialog(t("Ny bedrift", "New company")) { close ->
        p(t("Bedriften får en egen Firmakode og starter tom. Du blir admin; inviter så bedriftens egne folk under Oversikt → Fordel arbeid.", "The company gets its own company code and starts empty. You become admin; then invite the company's own people under Overview → Assign work."), "muted small")
        field(t("Bedriftsnavn", "Company name"), name, "nc-name", placeholder = t("f.eks. Thorbjørnrud Hotell", "e.g. Thorbjørnrud Hotel")) { name = it }
        field(t("Sted / lager", "Place / store"), place, "nc-place", placeholder = t("f.eks. Jevnaker", "e.g. Jevnaker"), hint = t("Vises sammen med månedene", "Shown together with the months")) { place = it }
        val slug = companySlug(name)
        if (slug.isNotBlank()) p(t("Bedrifts-id: $slug", "Company id: $slug"), "muted tiny")
        errorText(error)
        dialogActions {
            button(t("Avbryt", "Cancel"), "btn text") { close() }
            button(if (busy) t("Oppretter …", "Creating …") else t("Opprett", "Create"), "btn primary", enabled = !busy) {
                val cleanName = name.trim()
                val cleanPlace = place.trim().ifBlank { cleanName }
                if (cleanName.length < 2 || companySlug(cleanName).isBlank()) { error = t("Skriv inn bedriftsnavnet.", "Enter the company name."); requestRender(); return@button }
                busy = true
                error = null
                requestRender()
                flushPendingCounts()
                cloud.createCompany(cleanName, cleanPlace) { result ->
                    busy = false
                    result.onSuccess { m ->
                        close()
                        activateCloud(m, cloud.isRemembered())
                        toast(t("${m.companyName} er opprettet. Firmakode: ${m.companyCode}", "${m.companyName} has been created. Company code: ${m.companyCode}"))
                    }.onFailure { error = it.message; requestRender() }
                }
            }
        }
    }
}
